import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { getCurrentWeekOf } from '../lib/planGenerator';
import { planToDinnerEvents } from '../lib/planLifecycle';
import { trackEvent } from '../lib/analytics';

// Data + lifecycle for the weekly planner board (MEA-179 / MEA-182). Loads the
// current week's meal_plan + its normalized dinner_events, and drives the
// draft → lock → revert transitions through the RPCs. Requires migrations
// 027/028 applied; if they aren't, calls surface an error (board falls back to
// its empty state rather than crashing).
export function useWeeklyBoard(householdId) {
  const weekOf = getCurrentWeekOf();
  const [plan, setPlan] = useState(null); // { id, status, week_of, locked_at }
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false); // an action is in flight
  const [error, setError] = useState(null);

  const fetch = useCallback(async () => {
    if (!householdId) { setLoading(false); return; }
    setLoading(true);
    const { data: planRow, error: planErr } = await supabase
      .from('meal_plans')
      .select('id, status, week_of, locked_at')
      .eq('household_id', householdId)
      .eq('week_of', weekOf)
      .maybeSingle();
    if (planErr) { setError(planErr.message); setLoading(false); return; }
    setPlan(planRow || null);
    if (planRow) {
      const { data: evs, error: evErr } = await supabase
        .from('dinner_events')
        .select('*')
        .eq('meal_plan_id', planRow.id)
        .order('event_date', { ascending: true });
      if (evErr) { setError(evErr.message); setLoading(false); return; }
      setEvents(evs || []);
    } else {
      setEvents([]);
    }
    setError(null);
    setLoading(false);
  }, [householdId, weekOf]);

  useEffect(() => { fetch(); }, [fetch]);

  // Persist a freshly generated week: open (or reuse) the draft plan, then
  // replace its dinner_events with the generated nights.
  const persistDraft = useCallback(async (generatedPlan) => {
    if (!householdId) return;
    setBusy(true);
    try {
      const { data: planId, error: startErr } = await supabase.rpc('start_week', { p_week_of: weekOf });
      if (startErr) throw startErr;
      const { error: delErr } = await supabase.from('dinner_events').delete().eq('meal_plan_id', planId);
      if (delErr) throw delErr;
      const rows = planToDinnerEvents(generatedPlan, { mealPlanId: planId, householdId, weekOf });
      if (rows.length) {
        const { error: insErr } = await supabase.from('dinner_events').insert(rows);
        if (insErr) throw insErr;
      }
      trackEvent('plan_started', { week_of: weekOf, nights: rows.length });
      await fetch();
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setBusy(false);
    }
  }, [householdId, weekOf, fetch]);

  const runRpc = useCallback(async (fn, event) => {
    if (!plan) return;
    setBusy(true);
    try {
      const { error: e } = await supabase.rpc(fn, { p_meal_plan_id: plan.id });
      if (e) throw e;
      if (event) trackEvent(event, { week_of: weekOf });
      await fetch();
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setBusy(false);
    }
  }, [plan, weekOf, fetch]);

  const lockWeek = useCallback(() => runRpc('lock_week', 'plan_locked'), [runRpc]);
  const revertWeek = useCallback(() => runRpc('revert_week', null), [runRpc]);

  // ── Per-night edits (MEA-205) ──────────────────────────────────────────────
  // Change one dinner_events row without redrafting the week.
  const updateEvent = useCallback(async (id, patch) => {
    setBusy(true);
    try {
      const { error: e } = await supabase.from('dinner_events').update(patch).eq('id', id);
      if (e) throw e;
      await fetch();
    } catch (e) { setError(e?.message || String(e)); } finally { setBusy(false); }
  }, [fetch]);

  // Clear a night back to an open night (removes its row).
  const clearEvent = useCallback(async (id) => {
    setBusy(true);
    try {
      const { error: e } = await supabase.from('dinner_events').delete().eq('id', id);
      if (e) throw e;
      await fetch();
    } catch (e) { setError(e?.message || String(e)); } finally { setBusy(false); }
  }, [fetch]);

  // Fill an open night (inserts a row scoped to this plan/household).
  const fillNight = useCallback(async (eventDate, patch) => {
    if (!plan || !householdId) return;
    setBusy(true);
    try {
      const row = { meal_plan_id: plan.id, household_id: householdId, event_date: eventDate, slot: 'dinner', status: 'draft', ...patch };
      const { error: e } = await supabase.from('dinner_events').insert(row);
      if (e) throw e;
      await fetch();
    } catch (e) { setError(e?.message || String(e)); } finally { setBusy(false); }
  }, [plan, householdId, fetch]);

  return {
    weekOf,
    plan,
    events,
    status: plan?.status || null,
    loading,
    busy,
    error,
    persistDraft,
    lockWeek,
    revertWeek,
    updateEvent,
    clearEvent,
    fillNight,
    refetch: fetch,
  };
}
